import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './styles/lessonPage.scss'
import Collapsible from '../../components/Collapsible';
import PageHeader from '../../components/PageHeader';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import { readJson } from '../../apiHelper/readJson';

type Line = { name: string; url: string };

export default function LessonPage({ subject }: any) {
    const [title, setTitle] = useState("");
    const [studyText, setStudyText] = useState("");
    const [difficulty, setDifficulty] = useState(0);
    const [documents, setDocuments] = useState<Line[]>([]);
    const [videos, setVideos] = useState<Line[]>([]);
    const [bonuses, setBonuses] = useState<Line[]>([]);
    const [links, setLinks] = useState<Line[]>([]);
    const [id, setId] = useState("");
    const [completed, setCompleted] = useState(false);
    const [disable, setDisable] = useState(false);
    const [fetched, setFetched] = useState(false);

    const navigate = useNavigate();
    const { lessonNumber } = useParams();

    const api = new ApiHelper();

    function apply(lesson: any) {
        setTitle(lesson.title);
        setStudyText(lesson.studyText ?? "");
        setDifficulty(lesson.difficulty ?? 0);
        setDocuments(lesson.documents ?? []);
        setVideos(lesson.videos ?? []);
        setBonuses(lesson.bonuses ?? []);
        setLinks(lesson.links ?? []);
        setId(lesson.lessonId);
        setCompleted(lesson.isCompleted);
    }

    async function fetch() {
        try {
            const res = await api.lessonsApi.getLessonsDetail(subject);
            const proc = await readJson(res);
            const lesson = proc.lessons.filter((e: any) => e.number.toString() === lessonNumber)[0];
            if (lesson) apply(lesson);
        } catch (x: any) {
            api.processErrors(x);
        }
    }

    useEffect(() => {
        if (!fetched) {
            fetch();
            setFetched(true);
        }
    }, []);

    const ChangeState = async () => {
        if (disable) return;

        const next = !completed;
        setCompleted(next);
        setDisable(true);

        try {
            await api.lessonsApi.LessonStateCreate({ lessonId: id, completed: next });
        } catch (x: any) {
            api.processErrors(x);
            setCompleted(!next);
        }

        setDisable(false);
    };

    const openLink = (url: string) => { window.open(url, "_blank"); };
    // Documents (and bonuses) always open in the in-app viewer, whether they're an uploaded
    // file or a pasted link — DocumentViewer handles both (fetched blob vs. direct URL) so
    // the PDF is always viewed in place, never downloaded. Only "Odkazy" open externally.
    const openFile = (url: string) => {
        localStorage.setItem("url", url);
        navigate("dokument");
    };
    // Videos play in the in-app player either way — ReactPlayer handles both uploaded files
    // (resolved against filesUrl) and external links (YouTube, Vimeo, direct URLs).
    const openVideo = (url: string) => { localStorage.setItem("url", url); navigate("video"); };

    return (
        <div className="lessonPage">
            <Link to=".." className="backLink">← Zpět na lekce</Link>
            <PageHeader
                title={title}
                subtitle={difficulty > 0 ? "Náročnost " + "●".repeat(difficulty) + "○".repeat(Math.max(0, 3 - difficulty)) : undefined}
                right={
                    <div className={"completeToggle " + (completed ? "done" : "")} onClick={() => ChangeState()}>
                        <img src={completed ? "/images/pages/completed.png" : "/images/pages/incompleted.png"} alt="" />
                        <span>{completed ? "Hotovo" : "Označit jako hotové"}</span>
                    </div>
                }
            />

            {studyText &&
                <Collapsible title="Studijní text">
                    <div className="studyText">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{studyText}</ReactMarkdown>
                    </div>
                </Collapsible>
            }

            <Collapsible title="Dokumenty">
                {documents.map((e, i) => (
                    <div key={i} className="line" onClick={() => openFile(e.url)}>
                        <img src="/images/pages/text.png" alt="" />
                        <h2>{e.name}</h2>
                    </div>
                ))}
            </Collapsible>

            <Collapsible title="Videa">
                {videos.map((e, i) => (
                    <div key={i} className="line" onClick={() => openVideo(e.url)}>
                        <img src="/images/pages/play.png" alt="" />
                        <h2>{e.name}</h2>
                    </div>
                ))}
            </Collapsible>

            <Collapsible title="Bonusy">
                {bonuses.map((e, i) => (
                    <div key={i} className="line" onClick={() => openFile(e.url)}>
                        <img src="/images/pages/text.png" alt="" />
                        <h2>{e.name}</h2>
                    </div>
                ))}
            </Collapsible>

            <Collapsible title="Odkazy">
                {links.map((e, i) => (
                    <div key={i} className="line" onClick={() => openLink(e.url)}>
                        <img src="/images/pages/link.png" alt="" />
                        <h2>{e.name}</h2>
                    </div>
                ))}
            </Collapsible>

            {id &&
                <Link to={"/kurz/procvicovani/kviz/" + id} className="practiceLink">
                    Procvičit tuto lekci →
                </Link>
            }
        </div>
    );
}
